import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SynthesisGroup } from '../api/types';
import { cellStyleToCss, mergeCellStyle } from '../utils/cellStyle';

function formatDate(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

const INDENT_PX = 14;

const COLUMN_COUNT = 9;

/**
 * Lecture seule, jamais de champ éditable : cette table ne modifie ni la
 * synthèse ni les plannings sources — voir SynthesisPage. Reprend la mise en
 * forme (`mergeCellStyle`/`cellStyleToCss`, comme TaskTable) et
 * l'indentation (`task.depth`, calculée côté serveur sur l'arbre complet du
 * diagramme source — voir FoldersService.getSynthesis/getWorkload) de
 * chaque tâche, pour que cette vue agrégée reste fidèle au tableau d'origine
 * plutôt qu'un texte brut aplati.
 *
 * `hideGroupHeader` : SynthesisPage affiche chaque groupe dans sa propre
 * carte avec un en-tête combiné (nom + bouton d'export PDF, comme
 * DiagramPage) — dans ce cas la ligne de nom interne à la table ferait
 * doublon. WorkloadPage, qui n'a pas cet en-tête, garde le défaut (visible).
 */
export function SynthesisTable({ groups, hideGroupHeader = false }: { groups: SynthesisGroup[]; hideGroupHeader?: boolean }) {
  const { t } = useTranslation();

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="task-table">
        <colgroup>
          <col style={{ width: 32 }} />
          <col style={{ width: 180 }} />
          <col style={{ width: 180 }} />
          <col style={{ width: 70 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 110 }} />
          <col style={{ width: 130 }} />
          <col style={{ width: 50 }} />
          <col style={{ width: 80 }} />
        </colgroup>
        <thead>
          <tr>
            <th>#</th>
            <th>{t('tasks.nameFr')}</th>
            <th>{t('tasks.nameEn')}</th>
            <th>{t('tasks.duration')}</th>
            <th>{t('tasks.startDate')}</th>
            <th>{t('tasks.endDate')}</th>
            <th>{t('tasks.predecessors')}</th>
            <th>%</th>
            <th>{t('tasks.clientMilestone')}</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <Fragment key={group.diagramId}>
              {!hideGroupHeader && (
                <tr className="synthesis-group-row">
                  <td colSpan={COLUMN_COUNT}>
                    <Link to={`/diagrams/${group.diagramId}`}>{group.diagramName}</Link>
                  </td>
                </tr>
              )}
              {group.tasks.length === 0 ? (
                <tr>
                  <td colSpan={COLUMN_COUNT} className="synthesis-empty">
                    {t('synthesis.noTask')}
                  </td>
                </tr>
              ) : (
                group.tasks.map((task) => (
                  <tr key={task.id} style={{ opacity: task.visible ? 1 : 0.5 }}>
                    <td>{task.number}</td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'nameFr'))}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: (task.depth ?? 0) * INDENT_PX }}>
                        {task.isGroup && <span>📁</span>}
                        {task.nameFr}
                      </div>
                    </td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'nameEn'))}>{task.nameEn}</td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'duration'))}>{task.durationDays ?? ''}</td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'start'))}>{formatDate(task.startDate)}</td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'end'))}>{formatDate(task.endDate)}</td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'predecessors'))}>{task.predecessorsText}</td>
                    <td style={cellStyleToCss(mergeCellStyle(task.formatting, 'progress'))}>{task.progressPct}</td>
                    <td style={{ textAlign: 'center', ...cellStyleToCss(mergeCellStyle(task.formatting, 'milestone')) }}>
                      {task.isClientMilestone ? '✓' : ''}
                    </td>
                  </tr>
                ))
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
